import type { RowData } from "@tanstack/react-table";
import { Columns3Cog, FilterX } from "lucide-react";
import React from "react";
import { Filter } from "../../Icons";
import { Badge } from "../Badge";
import { Button } from "../Button";
import { DebouncedInput } from "../DebouncedInput";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../DropdownMenu";
import type { Table } from "./features";
import { FilterInput } from "./FilterInput";

type ToolbarProps<TData extends RowData> = {
  table: Table<TData>;
  globalFilterLabel: string | undefined;
};

export function DataTableToolbar<TData extends RowData>({ table, globalFilterLabel }: Readonly<ToolbarProps<TData>>) {
  const extraFilters = table.getAllColumns().filter((column) => {
    const isFilterCol = column.columnDef.meta?.filter?.hideColumn;
    const isColumnHidden = !column.getIsVisible();
    const isFilterable = column.getCanFilter();
    return isFilterCol || (isColumnHidden && isFilterable);
  });

  return (
    <div className="flex shrink-0 items-center gap-1 border-b border-medium-gray">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="small" className="shrink-0 gap-1">
            <Columns3Cog />
            Columns
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent align="end" side="bottom">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {table
              .getAllFlatColumns()
              .filter((column) => (column.accessorFn !== undefined && column.getCanHide()) || column.columns.length > 0)
              .map((column) => {
                const isGroup = column.columns.length > 0;
                const label = typeof column.columnDef.header === "string" ? column.columnDef.header : column.id;

                if (isGroup) {
                  return (
                    <React.Fragment key={column.id}>
                      <DropdownMenuSeparator />
                      <DropdownMenuCheckboxItem
                        className="text-normal font-semibold text-gray capitalize"
                        checked={column.columns.every((col) => col.getIsVisible())}
                        // eslint-disable-next-line unicorn/no-array-for-each -- inline expression
                        onCheckedChange={(value) => column.columns.forEach((col) => col.toggleVisibility(value))}
                      >
                        {label}
                      </DropdownMenuCheckboxItem>
                    </React.Fragment>
                  );
                }

                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="text-normal capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(value)}
                  >
                    {label}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
      <DropdownMenu>
        <Badge
          className="shrink-0"
          badgeContent={extraFilters.some((column) => column.getIsFiltered())}
          badgeClassName="top-1 right-1 h-2 min-w-2"
        >
          <DropdownMenuTrigger asChild>
            <Button size="small" className="gap-1" disabled={extraFilters.length === 0}>
              <Filter />
              Extra Filters
            </Button>
          </DropdownMenuTrigger>
        </Badge>
        <DropdownMenuPortal>
          <DropdownMenuContent align="end" side="bottom" className="flex w-60 flex-col gap-1">
            <DropdownMenuLabel>Extra Filters</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {extraFilters.map((column) => {
              const filter = column.columnDef.meta?.filter;
              if (!filter) return null;
              const colName = typeof column.columnDef.header === "string" ? column.columnDef.header : column.id;
              filter.name ??= colName;
              return (
                <div key={filter.key} role="presentation" onKeyDown={(e) => e.stopPropagation()}>
                  <FilterInput
                    filter={filter}
                    name={filter.name ?? colName}
                    value={column.getFilterValue()}
                    onValueChange={(value) => column.setFilterValue(value)}
                  />
                </div>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
      <Button
        size="small"
        className="shrink-0 gap-1"
        disabled={!table.state.columnFilters?.length}
        onClick={() => {
          table.resetGlobalFilter();
          table.resetColumnFilters();
        }}
      >
        <FilterX />
        Clear filters
      </Button>
      {table.state.globalFilter != null && (
        <DebouncedInput
          className="m-1 [&>input]:py-1"
          placeholder={globalFilterLabel}
          value={table.state.globalFilter}
          onValueChange={(value) => table.setGlobalFilter(value)}
        />
      )}
    </div>
  );
}
